import { RootState } from '@AppState/redux-store'
import { ApiKeysServiceAPI } from '@API/data-services/api-keys-service'
import { readErrorMessage } from '@Components/util/utils'
import { ServiceName } from '@Generated/back-office-client/src'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

export const NO_SCOPES_MESSAGE = 'No scopes available for this service'

/** The scopes a key can be given, and why they could not be loaded when that fails. */
export type AvailableScopes = {
  availableScopes?: string[]
  /** True until the scopes, or the reason they failed, are known */
  scopesLoading: boolean
  scopesErrorMessage: string | null
}

export const useAvailableScopes = (service: ServiceName | undefined): AvailableScopes => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const [scopesErrorMessage, setScopesErrorMessage] = useState<string | null>(null)

  const {
    data: availableScopes,
    isLoading: scopesLoading,
    error: scopesError,
  } = useQuery({
    queryKey: [`${activeCustomerId}-api-key-scopes`, service],
    queryFn: () => ApiKeysServiceAPI.getAvailableScopes(service!),
    enabled: !!service,
  })

  // The message comes out of the response body, which reads asynchronously; a newer error or none replaces a pending read
  useEffect(() => {
    let current = true
    if (!scopesError) {
      setScopesErrorMessage(null)
    } else {
      readErrorMessage(scopesError, NO_SCOPES_MESSAGE).then((message) => {
        if (current) {
          setScopesErrorMessage(message)
        }
      })
    }
    return () => {
      current = false
    }
  }, [scopesError])

  return {
    availableScopes,
    scopesLoading: scopesLoading || (!!scopesError && scopesErrorMessage === null),
    scopesErrorMessage,
  }
}
